import { ScrollingModule } from '@angular/cdk/scrolling';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import localeFrExtra from '@angular/common/locales/extra/fr';
import localeFr from '@angular/common/locales/fr';
import { CUSTOM_ELEMENTS_SCHEMA, DEFAULT_CURRENCY_CODE, LOCALE_ID, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatNativeDateModule, MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';

import { MatExpansionModule } from '@angular/material/expansion';

import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonToggleModule } from '@angular/material/button-toggle';

import { MatStepperModule } from '@angular/material/stepper';
import { MatBadgeModule } from '@angular/material/badge';

import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ErrorComponent } from './error.component';
import { CoreData } from './tools/core-data/core-data';
import { DragDropDirective } from './tools/directive/drag-drop-directive';
import { PreventDoubleClickDirective } from './tools/directive/prevent-double-click.directive';
import { LoginGuard } from './tools/login-guard';
import { ConfirmBoxDialog } from './tools/ui/confirm-box/confirm-box.component';
import { LoadingTask } from './tools/ui/loading-task';
import { ClipboardModule } from '@angular/cdk/clipboard';

import { MatSortModule } from '@angular/material/sort';
import { TextFieldModule } from '@angular/cdk/text-field';
import { NavMenuComponent } from './components/nav-menu/nav-menu.component';
import { ServiceWorkerModule, SwRegistrationOptions } from '@angular/service-worker';
import { environment } from 'src/environments/environment';
import { InterventionListComponent } from './components/intervention-list/intervention-list.component';
import { WeeklyListComponent } from './components/weekly-list/weekly-list.component';
import { InterventionComponent } from './components/intervention/intervention.component';
import { RedirectComponent } from './components/redirect/redirect.component';
import { CustomSnackBarComponent } from './tools/ui/custom-snackbar/custom-snackbar.component';
import { ZoneConformiteBoxDialog } from './tools/ui/zone-conformite-box/zone-conformite-box';
import { ZoneComponent } from './components/zone/zone.component';
import { PlageHoraireBoxDialog } from './tools/ui/plage-horaire-box/plage-horaire-box';
import { ProcessConformiteBoxDialog } from './tools/ui/process-conformite-box/process-conformite-box';
import { ProcessComponent } from './components/process/process.component';
import { TaskBoxDialog } from './tools/ui/add-task-box/add-task-box';
import { DrawingMapToolComponent } from './components/drawing-map-tool/drawing-map-tool.component';
import { PointageComponent } from './components/pointage/pointage.component';
import { PointageBoxDialog } from './tools/ui/pointage-box/pointage-box';
import { AddMPCABoxDialog } from './tools/ui/add-mpca-box/add-mpca-box';
import { ScanComponent } from './components/scan/scan.component';
import { WebcamModule } from 'ngx-webcam';
import { CopyPlageHorraireBetweenZonesBoxDialog } from './tools/ui/copy-ph-between-zones-box/copy-ph-between-zones-box';
import { MassifComponent } from './components/massif/massif.component';
import { LingetteComponent } from './components/lingette/lingette.component';
import { EauComponent } from './components/eau/eau.component';
import { InitOperatorBoxDialog } from './tools/ui/init-operator-box/init-operator-box';
import { AppareilMesureComponent } from './components/metrologie/appareil-mesure.component';
import { NumberDirective } from './tools/directive/number-only.directive';
import { MultipleFileUploadBoxDialog } from './tools/ui/mutilple-file-upload-box/multiple-file-upload-box';
import { EditTextBoxDialog } from './tools/ui/edit-text-box/edit-text-box';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AdductionComponent } from './components/adduction/adduction.component';
import { AdductionMaterielBoxDialog } from './tools/ui/adduction-materiel-box/adduction-materiel-box';
import { AdductionPrelevementBoxDialog } from './tools/ui/adduction-prelevement-box/adduction-prelevement-box';
import { GenericsComponent } from './components/generics/generics.component';
import { DndModule } from 'ngx-drag-drop';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NgxMaterialTimepickerModule } from '@gerandon/ngx-material-timepicker';

const globalRippleConfig: RippleGlobalOptions = {
    disabled: true,
    animation: {
        enterDuration: 300,
        exitDuration: 0
    }
};

@NgModule({ declarations: [
        AppComponent,
        CustomSnackBarComponent,
        NavMenuComponent,
        ErrorComponent,
        PreventDoubleClickDirective,
        NumberDirective,
        DragDropDirective,
        ConfirmBoxDialog,
        ZoneConformiteBoxDialog,
        ProcessConformiteBoxDialog,
        PlageHoraireBoxDialog,
        TaskBoxDialog,
        InitOperatorBoxDialog,
        InterventionListComponent,
        WeeklyListComponent,
        InterventionComponent,
        MultipleFileUploadBoxDialog,
        RedirectComponent,
        ZoneComponent,
        ProcessComponent,
        DrawingMapToolComponent,
        PointageComponent,
        PointageBoxDialog,
        AddMPCABoxDialog,
        ScanComponent,
        CopyPlageHorraireBetweenZonesBoxDialog,
        MassifComponent,
        LingetteComponent,
        EauComponent,
        AppareilMesureComponent,
        EditTextBoxDialog,
        AdductionComponent,
        AdductionMaterielBoxDialog,
        AdductionPrelevementBoxDialog,
        GenericsComponent
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA], 
    imports: [
        BrowserModule,
        AppRoutingModule,
        CommonModule,
        FormsModule,
        BrowserModule,
        ReactiveFormsModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        ClipboardModule,
        MatTableModule,
        MatSnackBarModule,
        MatFormFieldModule,
        MatExpansionModule,
        MatInputModule,
        MatButtonModule,
        MatProgressBarModule,
        MatTooltipModule,
        MatDialogModule,
        MatIconModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatPaginatorModule,
        MatAutocompleteModule,
        MatSelectModule,
        MatSortModule,
        MatChipsModule,
        MatSlideToggleModule,
        MatButtonToggleModule,
        MatStepperModule,
        MatListModule,
        MatBadgeModule,
        MatMenuModule,
        TextFieldModule,
        ScrollingModule,
        MatCheckboxModule,
        FlexLayoutModule,
        WebcamModule,
        DndModule,
        DragDropModule,
        NgxMaterialTimepickerModule,
        ServiceWorkerModule.register('ngsw-worker.js')], 
    providers: [
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
        { provide: LOCALE_ID, useValue: 'fr-FR' },
        // { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
        { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
        { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
        {
            provide: SwRegistrationOptions,
            useFactory: () => ({ enabled: environment.production }),
        },
        CoreData,
        LoadingTask,
        LoginGuard,
        DatePipe,
        provideHttpClient(withXhr(), withInterceptorsFromDi())
    ] })
export class AppModule { }

registerLocaleData(localeFr, 'fr', localeFrExtra);
